import { useHistory } from 'react-router-dom';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ArcRoutes } from '../../pages/arc/constants/routes';
import { YesNoIndicators } from '../models/api-model';
import { TcsmStates } from '../../root/tcsm-model';

let unfinishedProcess: ProcessName = null;
let unfinishedProcessUrl: string = null;

let currentAnalyticsClientGuid: string = null;

interface AnalyticEvent {
	action: string,
}

export enum EventType {
	GpsClick = 'gps_click',
	PdfOpened = 'pdf_open',
	ExternalLink = 'external_link_click',
	AbandonProcess = 'abandon_process',
	StartProcess = 'startProcess',
	CompleteProcess = 'completeProcess',
}

export enum ProcessName {
	PersonalInformation = 'personal_information',
	MaritalStatus = 'marital_status',
	DependentAdd = 'add_dependent',
	DependentUpdate = 'update_dependent',
	OneTimePayment = 'one_time_payment',
	ScheduledHours = 'scheduled_hours',
	ScheduledPayment = 'scheduled_payment',
	Pension = 'pension',
}

export function RegisterEvent(type: string, value: AnalyticEvent) {
	const dataLayerCache = (window as any).dataLayer;
	if (type && value && dataLayerCache) {
		dataLayerCache.push({
			'event': 'zaiusEvent',
			'zaiusEventType': type,
			'zaiusAction': value?.action,
			'zaiusURL': (value as any)?.page_url,
		});

		if (type === EventType.CompleteProcess) {
			dataLayerCache.push({
				'event': 'formSubmitSuccess',
				'customer_guid': currentAnalyticsClientGuid,
				'form_name': value?.action || (value as any)?.page_url,
			});
		}

		if (type === EventType.AbandonProcess) {
			dataLayerCache.push({
				'event': 'formSubmitAbandon',
				'customer_guid': currentAnalyticsClientGuid,
				'form_name': value?.action || (value as any)?.page_url,
			});
		}
	}
}

export function RegisterClick(type: EventType, action: string, page_url: string) {
	const eventArgs = {
		action,
		page_url,
	};

	RegisterEvent(type, eventArgs);
}

export function RegisterLinkClick(action: string, page_url: string) {
	const isPDF = page_url?.toLocaleLowerCase().includes('.pdf');
	const isExternal = !page_url?.includes(window.location.host);

	const eventArgs = {
		action,
		page_url,
	};

	if (isExternal) {
		RegisterEvent(EventType.ExternalLink, eventArgs);
		return;
	}

	if (isPDF) {
		RegisterEvent(EventType.PdfOpened, eventArgs);
	}
}

function RegisterAbandonProcess(processName: ProcessName, didCloseBrowser: boolean) {
	const eventArgs = {
		action: processName,
		page_url: unfinishedProcessUrl,
		didCloseBrowser,
	};

	RegisterEvent(EventType.AbandonProcess, eventArgs);

	unfinishedProcess = null;
	unfinishedProcessUrl = null;
}

export function RegisterStartProcess(processName: ProcessName) {

	const newUnfinishedProcessUrl = window.location?.pathname;

	if ((unfinishedProcess !== processName) &&
		(unfinishedProcessUrl !== newUnfinishedProcessUrl)) {

		const eventArgs = {
			action: processName,
			page_url: newUnfinishedProcessUrl,
		};

		RegisterEvent(EventType.StartProcess, eventArgs);

		if (unfinishedProcess) {
			RegisterAbandonProcess(unfinishedProcess, false);
		}
	}

	unfinishedProcess = processName;
	unfinishedProcessUrl = newUnfinishedProcessUrl;
}

export function RegisterCompletedProcess(processName: ProcessName) {

	const eventArgs = {
		action: processName,
		page_url: unfinishedProcessUrl,
	};

	RegisterEvent(EventType.CompleteProcess, eventArgs);

	if (unfinishedProcess && unfinishedProcess !== processName) {
		RegisterAbandonProcess(unfinishedProcess, false);
	}

	unfinishedProcess = null;
	unfinishedProcessUrl = null;
}

export function HandleOnBeforeUnload() {
	if (unfinishedProcess) {
		RegisterAbandonProcess(unfinishedProcess, true);
	}
}

let lastPageUrl = null;

export function trackPageView(clergyStatus?: string, roles?: string[]) {
	const dataLayerCache = (window as any).dataLayer;
	const pageUrl = window.location.pathname?.toLocaleLowerCase();
	if (dataLayerCache && lastPageUrl !== pageUrl) {

		lastPageUrl = pageUrl;
		const pageTile = document?.title;

		dataLayerCache.push({
			'event': 'zaiusEvent',
			'zaiusEventType': 'page_view',
			'zaiusAction': pageTile,
			'zaiusURL': pageUrl,
		});

		const gaPayload: any = {
			'event': 'UAPageview',
			'logged_in': 'yes',
			'pageURL': pageUrl,
			'pageTitle': pageTile,
		};

		if (pageUrl?.includes(ArcRoutes.ClergyBio)) {
			gaPayload.clergyType = clergyStatus;
		}

		if (roles?.length > 0) {
			for (let i = 0; i < roles?.length; i += 1) {
				gaPayload[roles[i]] = 'yes';
			}
		}

		dataLayerCache.push(gaPayload);
	}
}

export function RouteHistoryTracking() {
	const history = useHistory();
	const isRetired = useSelector<TcsmStates, YesNoIndicators>(states => states?.authenticatedState?.benefitFacts?.segment_clergy_retired);
	const roles = useSelector<TcsmStates, string[]>(states => states?.authenticatedState?.roles);
	const clientGuid = useSelector<TcsmStates, string>(states => states?.authenticatedState?.clientGuid);
	const clergyStatus = isRetired === YesNoIndicators.Yes ? 'Retired' : 'Active';
	useEffect(() => {
		if (roles && history) {
			trackPageView(clergyStatus, roles); // To track the first pageview upon load
			history.listen(() => trackPageView(clergyStatus, roles)); // To track the subsequent pageviews
		}
	}, [history, roles]);

	useEffect(() => {
		if (clientGuid) {
			currentAnalyticsClientGuid = clientGuid;
		}

	}, [clientGuid]);



	return null;
}

export enum ClergyBioGaFormTypes {
	Personal = 'Personal',
	Ministry = 'Ministry',
	Education = 'Education',
	ChurchReporting = 'Church Reporting',
	Relationship = 'Relationship',
	Professional = 'Professional',
	Volunteer = 'Volunteer',
}

export function HandleClergyBioSubmit(form: ClergyBioGaFormTypes, guid: string) {
	const dataLayerCache = (window as any)?.dataLayer;
	if (dataLayerCache) {
		dataLayerCache.push({
			'event': 'formSubmitSuccess',
			'form_name': form,
			'customer_guid': guid,
		});
	}

}

export function RegisterLogin(cn:string, email: string) {
	const dataLayerCache = (window as any).dataLayer;
	if (dataLayerCache) {
		dataLayerCache.push({
			'customer_id': cn,
			'customer_email': email,
			'event': 'customer_login',
		});
	}

}